import { isAxiosError } from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import { z } from 'zod'
import {
  resolveLanguage,
  SUPPORTED_LANG_CODES,
  type SupportedLangCode,
} from '@/application/i18n/language'
import { taxonomyKeys } from '@/application/services/taxonomy.service'
import type {
  Investigation,
  InvestigationRefResolution,
  InvestigationSlugs,
  InvestigationRead,
  CreateInvestigationFormData,
  TranslationsWritePayload,
  CategoryLabelsWritePayload,
} from '@/domains/types'

const INVESTIGATION_API_BASE = '/investigations'

export const investigationKeys = {
  // Locale-scoped: a language switch must not serve a previous locale's
  // translated fields from the TanStack cache (TRANSLATION-PLAN.md §9
  // Phase 1). Every other key below stays locale-agnostic — either the
  // response never carries translatable content, or (byExternalId/byName)
  // the endpoint doesn't yet accept `?lang=`.
  byId: (entityId: string, locale: string) => ['investigations', entityId, locale] as const,
  // No-locale prefix: what mutations invalidate, so every cached locale
  // variant for this investigation is dropped together.
  byIdPrefix: (entityId: string) => ['investigations', entityId] as const,
  // Source-language content, read without `?lang=`. Lives under byIdPrefix so
  // every mutation that invalidates the prefix refreshes it too.
  forEdit: (entityId: string) => ['investigations', entityId, 'edit'] as const,
  read: (entityId: string, locale: string) => ['investigations', entityId, 'read', locale] as const,
  // Locale-scoped for the same reason as byId/read above — the management
  // investigations list must react to a language switch.
  all: (locale: string) => ['investigations', 'all', locale] as const,
  // No-locale prefix: what mutations invalidate, so every cached locale
  // variant of the full list is dropped together.
  allPrefix: ['investigations', 'all'] as const,
  byExternalId: (investigationId: string) =>
    ['investigations', 'by-external-id', investigationId] as const,
  byName: (name: string) => ['investigations', 'by-name', name] as const,
  search: (query: string) => ['investigations', 'search', query] as const,
  withCounts: () => ['investigations', 'with-counts'] as const,
  // Public URL segment (slug of any language or UUID) -> canonical identifiers.
  resolve: (reference: string, locale: string) =>
    ['investigations', 'resolve', reference, locale] as const,
  resolvePrefix: ['investigations', 'resolve'] as const,
}

const refResolutionSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  canonical: z.boolean(),
  slugs: z.record(z.string(), z.string()).nullish(),
})

/** Validates the resolve payload; an unexpected shape throws (the caller falls back to the UUID). */
function parseRefResolution(data: unknown, locale: SupportedLangCode): InvestigationRefResolution {
  const parsed = refResolutionSchema.parse(data)
  const slugs: InvestigationSlugs = {}
  for (const code of SUPPORTED_LANG_CODES) {
    const value = parsed.slugs?.[code]
    if (value) slugs[code] = value
  }
  return { id: parsed.id, locale, slug: parsed.slug, slugs, canonical: parsed.canonical }
}

/**
 * Resolves a public URL segment (slug of any language, historical slug or
 * UUID) for the route language. `null` when unknown (404). Plain function (not
 * a hook) so the route `loader` can call it.
 */
export async function resolveInvestigationRef(
  reference: string,
  locale: SupportedLangCode,
): Promise<InvestigationRefResolution | null> {
  try {
    const response = await apiClient.get<unknown>(`${INVESTIGATION_API_BASE}/resolve`, {
      params: { ref: reference, lang: locale },
    })
    return parseRefResolution(response.data, locale)
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null
    throw error
  }
}

// Get all investigations
export function useGetAllInvestigations() {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: investigationKeys.all(locale),
    queryFn: async (): Promise<Investigation[]> => {
      const response = await apiClient.get<Investigation[]>(INVESTIGATION_API_BASE, {
        params: { lang: locale },
      })
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Get investigation by ID
export function useGetInvestigationById(entityId: string) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: investigationKeys.byId(entityId, locale),
    queryFn: async (): Promise<Investigation | null> => {
      const response = await apiClient.get<Investigation>(`${INVESTIGATION_API_BASE}/${entityId}`, {
        params: { lang: locale },
      })
      return response.data
    },
    enabled: !!entityId,
  })
}

// Get an investigation for EDITING: always the stored source-language content.
// `useGetInvestigationById` sends `?lang=<UI locale>`, so when the admin UI
// language differs from the investigation's source language its name and
// description come back translated (or fallen back), and saving the form
// would overwrite the source text with that. Editing must never use it.
export function useGetInvestigationForEdit(entityId: string) {
  return useQuery({
    queryKey: investigationKeys.forEdit(entityId),
    queryFn: async (): Promise<Investigation | null> => {
      const response = await apiClient.get<Investigation>(`${INVESTIGATION_API_BASE}/${entityId}`)
      return response.data
    },
    enabled: !!entityId,
    // The form is hydrated once from this; never swap values under the user's
    // hands on a window refocus.
    refetchOnWindowFocus: false,
  })
}

// Get the compiled read-mode view of an investigation (investigation + leads + facts + source).
// `publicOnly` forces the anonymous published-only view even for a privileged viewer, so an
// admin/streamer/author can preview what a public visitor sees regardless of their own session.
export function useGetInvestigationRead(entityId: string, publicOnly = false) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: publicOnly
      ? [...investigationKeys.read(entityId, locale), 'public']
      : investigationKeys.read(entityId, locale),
    queryFn: async (): Promise<InvestigationRead> => {
      const response = await apiClient.get<InvestigationRead>(
        `${INVESTIGATION_API_BASE}/${entityId}/read`,
        { params: { lang: locale, ...(publicOnly ? { public: 'true' } : {}) } },
      )
      return response.data
    },
    enabled: !!entityId,
  })
}

// Get investigation by external ID
export function useGetInvestigationByExternalId(investigationId: string) {
  return useQuery({
    queryKey: investigationKeys.byExternalId(investigationId),
    queryFn: async (): Promise<Investigation | null> => {
      const response = await apiClient.get<Investigation>(
        `${INVESTIGATION_API_BASE}/by-external-id/${investigationId}`,
      )
      return response.data
    },
    enabled: !!investigationId,
  })
}

// Get investigation by name
export function useGetInvestigationByName(name: string) {
  return useQuery({
    queryKey: investigationKeys.byName(name),
    queryFn: async (): Promise<Investigation | null> => {
      const response = await apiClient.get<Investigation>(
        `${INVESTIGATION_API_BASE}/by-name/${encodeURIComponent(name)}`,
      )
      return response.data
    },
    enabled: !!name,
  })
}

// Search investigations
export function useSearchInvestigations(query: string, limit: number = 10) {
  return useQuery({
    queryKey: investigationKeys.search(query),
    queryFn: async (): Promise<Investigation[]> => {
      const response = await apiClient.get<Investigation[]>(`${INVESTIGATION_API_BASE}/search`, {
        params: { q: query, limit },
      })
      return response.data
    },
    enabled: !!query,
    staleTime: 1000 * 60 * 1, // 1 minute
  })
}

// Get investigations with lead counts
export function useGetInvestigationsWithCounts() {
  return useQuery({
    queryKey: investigationKeys.withCounts(),
    queryFn: async (): Promise<Array<Investigation & { leadCount: number }>> => {
      const response = await apiClient.get<Array<Investigation & { leadCount: number }>>(
        `${INVESTIGATION_API_BASE}/with-counts`,
      )
      return response.data
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

// Create investigation
export function useCreateInvestigation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateInvestigationFormData): Promise<Investigation> => {
      const response = await apiClient.post<Investigation>(INVESTIGATION_API_BASE, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: investigationKeys.allPrefix })
      queryClient.invalidateQueries({ queryKey: investigationKeys.withCounts() })
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

// Update investigation
export function useUpdateInvestigation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id: entityId,
      data,
    }: {
      id: string
      data: Partial<CreateInvestigationFormData> & {
        author?: string
        authorId?: string | null
        liveDate?: string | null
        twitchUrl?: string | null
        youtubeUrl?: string | null
        translations?: TranslationsWritePayload
        categoryLabels?: CategoryLabelsWritePayload
      }
    }): Promise<Investigation> => {
      const response = await apiClient.put<Investigation>(
        `${INVESTIGATION_API_BASE}/${entityId}`,
        data,
      )
      return response.data
    },
    onSuccess: (_result, { id: entityId }) => {
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(entityId) })
      queryClient.invalidateQueries({ queryKey: investigationKeys.allPrefix })
      queryClient.invalidateQueries({ queryKey: investigationKeys.withCounts() })
      queryClient.invalidateQueries({ queryKey: investigationKeys.resolvePrefix })
      queryClient.invalidateQueries({ queryKey: ['translations', 'investigation', entityId] })
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

// Update investigation notes (markdown)
export function useUpdateInvestigationNotes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id: entityId,
      notes,
    }: {
      id: string
      notes: string
    }): Promise<{ notes: string | null }> => {
      const response = await apiClient.patch<{ notes: string | null }>(
        `${INVESTIGATION_API_BASE}/${entityId}/notes`,
        { notes },
      )
      return response.data
    },
    onSuccess: (_result, { id: entityId }) => {
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(entityId) })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

// Delete investigation
export function useDeleteInvestigation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entityId: string): Promise<boolean> => {
      const response = await apiClient.delete<boolean>(`${INVESTIGATION_API_BASE}/${entityId}`)
      return response.data
    },
    onSuccess: (_result, entityId) => {
      queryClient.invalidateQueries({ queryKey: investigationKeys.allPrefix })
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(entityId) })
      queryClient.invalidateQueries({ queryKey: investigationKeys.withCounts() })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

// ─── Active investigation ─────────────────────────────────────────────────────

export function useGetActiveInvestigation() {
  return useQuery({
    queryKey: ['investigations', 'active'],
    queryFn: async (): Promise<Investigation | null> => {
      try {
        const response = await apiClient.get<Investigation>('/investigations/active')
        return response.data ?? null
      } catch {
        return null
      }
    },
    staleTime: 1000 * 30,
  })
}

export function useSetActiveInvestigation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entityId: string): Promise<Investigation> => {
      const response = await apiClient.post<Investigation>(`/investigations/${entityId}/activate`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations', 'active'] })
      queryClient.invalidateQueries({ queryKey: ['investigations'] })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useSetActiveLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      investigationId,
      leadId,
    }: {
      investigationId: string
      leadId: string | null
    }): Promise<Investigation> => {
      const response = await apiClient.post<Investigation>(
        `/investigations/${investigationId}/activate-lead`,
        { leadId },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations', 'active'] })
      queryClient.invalidateQueries({ queryKey: ['investigations'] })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useSetActiveFact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      investigationId,
      factId,
    }: {
      investigationId: string
      factId: string | null
    }): Promise<Investigation> => {
      const response = await apiClient.post<Investigation>(
        `/investigations/${investigationId}/activate-fact`,
        { factId },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['investigations', 'active'] })
      queryClient.invalidateQueries({ queryKey: ['investigations'] })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

// Toggle investigation active/inactive status
export function useToggleInvestigationStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entityId: string): Promise<Investigation> => {
      const response = await apiClient.patch<Investigation>(
        `${INVESTIGATION_API_BASE}/${entityId}/toggle-status`,
      )
      return response.data
    },
    onSuccess: (_result, entityId) => {
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(entityId) })
      queryClient.invalidateQueries({ queryKey: investigationKeys.allPrefix })
      queryClient.invalidateQueries({ queryKey: investigationKeys.withCounts() })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

// Set investigation conclusion
export function useSetInvestigationConclusion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      conclusion,
    }: {
      id: string
      conclusion: 'true' | 'false' | 'fallacious' | 'completed' | null
    }): Promise<Investigation> => {
      const response = await apiClient.patch<Investigation>(
        `${INVESTIGATION_API_BASE}/${id}/conclusion`,
        { conclusion },
      )
      return response.data
    },
    onSuccess: (_result, { id }) => {
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(id) })
      queryClient.invalidateQueries({ queryKey: investigationKeys.allPrefix })
      queryClient.invalidateQueries({ queryKey: investigationKeys.withCounts() })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

// Set investigation status (pending | active | inactive)
export function useSetInvestigationStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id: entityId,
      status,
    }: {
      id: string
      status: 'pending' | 'active' | 'inactive'
    }): Promise<Investigation> => {
      const response = await apiClient.patch<Investigation>(
        `${INVESTIGATION_API_BASE}/${entityId}/set-status`,
        { status },
      )
      return response.data
    },
    onSuccess: (_result, { id: entityId }) => {
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(entityId) })
      queryClient.invalidateQueries({ queryKey: investigationKeys.allPrefix })
      queryClient.invalidateQueries({ queryKey: investigationKeys.withCounts() })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}
